import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Eye, EyeOff, Check, LinkIcon, Edit, Save, Plus, Trash2, X } from 'lucide-react';
import Navbar from '../../layout/Navbar';
import { testDataApi } from '../../../api/testdata';

const TestDataDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [fixture, setFixture] = useState(null);
  const [linkedSuites, setLinkedSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSuites, setLoadingSuites] = useState(true);
  const [error, setError] = useState(null);
  const [visibleValues, setVisibleValues] = useState(new Set());
  const [copiedKey, setCopiedKey] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFixture, setEditedFixture] = useState(null);
  const [tempKeys, setTempKeys] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    fetchFixtureDetails();
    fetchLinkedSuites();
  }, [id]);

  const fetchFixtureDetails = async () => {
    try {
      setLoading(true);
      const data = await testDataApi.getFixtureById(id);
      setFixture(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch fixture details:', err);
      setError('Failed to load fixture details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedSuites = async () => {
    try {
      setLoadingSuites(true);
      const data = await testDataApi.getFixtureLinkedSuites(id);
      
      // Handle different response formats
      if (Array.isArray(data)) {
        // Direct array format
        setLinkedSuites(data.map(suite => ({
          id: suite.id,
          name: suite.suite_name || suite.name,
          is_default: suite.is_default || false
        })));
      } else if (data?.linked_suites) {
        // Nested format
        setLinkedSuites(data.linked_suites);
      } else {
        setLinkedSuites([]);
      }
    } catch (err) {
      console.error('Failed to fetch linked test suites:', err);
      // Don't set an error here to avoid blocking the main fixture display
    } finally {
      setLoadingSuites(false);
    }
  };

  const toggleValueVisibility = (key) => {
    const newVisible = new Set(visibleValues);
    if (newVisible.has(key)) {
      newVisible.delete(key);
    } else {
      newVisible.add(key);
    }
    setVisibleValues(newVisible);
  };

    const handleCopyValue = (key, value) => {
    navigator.clipboard.writeText(value.toString());
    setCopiedKey(`value_${key}`);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const startEditing = () => {
    setEditedFixture({
      name: fixture.name,
      description: fixture.description,
      variables: { ...fixture.variables }
    });
    
    // Initialize tempKeys with the current keys
    const initialTempKeys = {};
    Object.keys(fixture.variables).forEach(key => {
      initialTempKeys[key] = key;
    });
    setTempKeys(initialTempKeys);
    
    setIsEditMode(true);
  };

  const cancelEditing = () => {
    setEditedFixture(null);
    setIsEditMode(false);
    setSaveError(null);
  };

  const handleFieldChange = (field, value) => {
    setEditedFixture(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleVariableChange = (key, value) => {
    setEditedFixture(prev => ({
      ...prev,
      variables: {
        ...prev.variables,
        [key]: value
      }
    }));
  };

  // Handle input changes for variable keys while editing
  const handleKeyInputChange = (oldKey, newValue) => {
    setTempKeys(prev => ({
      ...prev,
      [oldKey]: newValue
    }));
  };

  // Handle the actual key change when the field loses focus
  const handleKeyBlur = (oldKey) => {
    const newKey = tempKeys[oldKey];
    
    if (oldKey === newKey || !newKey || !newKey.trim()) return;
    
    // Update the editedFixture with the new key
    setEditedFixture(prev => {
      const updatedVariables = { ...prev.variables };
      const value = updatedVariables[oldKey];
      delete updatedVariables[oldKey];
      updatedVariables[newKey] = value;
      
      return {
        ...prev,
        variables: updatedVariables
      };
    });
    
    // Update tempKeys to reflect the new structure
    setTempKeys(prevKeys => {
      const updatedKeys = { ...prevKeys };
      delete updatedKeys[oldKey];
      updatedKeys[newKey] = newKey;
      return updatedKeys;
    });
  };

  // Legacy function kept for backward compatibility
  const handleVariableKeyChange = (oldKey, newKey) => {
    if (oldKey === newKey) return;
    
    setEditedFixture(prev => {
      const updatedVariables = { ...prev.variables };
      const value = updatedVariables[oldKey];
      delete updatedVariables[oldKey];
      updatedVariables[newKey] = value;
      
      return {
        ...prev,
        variables: updatedVariables
      };
    });
  };

  const addNewVariable = () => {
    const newKey = `new_variable_${Object.keys(editedFixture.variables).length + 1}`;
    
    // Update the fixture data
    setEditedFixture(prev => ({
      ...prev,
      variables: {
        ...prev.variables,
        [newKey]: ''
      }
    }));
    
    // Also update tempKeys
    setTempKeys(prev => ({
      ...prev,
      [newKey]: newKey
    }));
  };

  const removeVariable = (key) => {
    // Update the fixture data
    setEditedFixture(prev => {
      const updatedVariables = { ...prev.variables };
      delete updatedVariables[key];
      
      return {
        ...prev,
        variables: updatedVariables
      };
    });
    
    // Also clean up tempKeys
    setTempKeys(prev => {
      const updatedKeys = { ...prev };
      delete updatedKeys[key];
      return updatedKeys;
    });
  };

  const saveChanges = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      
      await testDataApi.updateFixture(
        id,
        editedFixture.name,
        editedFixture.description,
        editedFixture.variables
      );
      
      // Reload fixture data
      await fetchFixtureDetails();
      
      setIsEditMode(false);
      setEditedFixture(null);
    } catch (err) {
      console.error('Failed to update fixture:', err);
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Sidebar - hidden on narrow screens */}
        <div className="hidden lg:block w-64 flex-shrink-0 bg-gray-50 border-r border-gray-200 min-h-screen p-4">
          <div className="space-y-1">
            <Link to="/testdata" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Fixtures
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
              {error}
            </div>
          ) : fixture ? (
            <div className="max-w-4xl w-full min-w-0">
              {/* Back link for narrow screens */}
              <Link to="/testdata" className="lg:hidden block mb-4 text-sm text-gray-700 hover:text-gray-900 flex items-center">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Fixtures
              </Link>
              <div className="mb-8">
                {isEditMode ? (
                  <>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                      <h1 className="text-3xl font-bold">Edit Fixture</h1>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={cancelEditing}
                          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md font-medium"
                          disabled={isSaving}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveChanges}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center gap-2 font-medium"
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : (
                            <>
                              <Save className="w-5 h-5" />
                              Save Changes
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {saveError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                        {saveError}
                      </div>
                    )}
                    
                    <div className="space-y-6 mb-8">
                      <div>
                        <label htmlFor="fixtureName" className="block text-sm font-medium text-gray-700 mb-2">
                          Fixture Name
                        </label>
                        <input
                          id="fixtureName"
                          type="text"
                          value={editedFixture.name}
                          onChange={(e) => handleFieldChange('name', e.target.value)}
                          className="block w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="fixtureDescription" className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          id="fixtureDescription"
                          value={editedFixture.description}
                          onChange={(e) => handleFieldChange('description', e.target.value)}
                          rows={3}
                          className="block w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div>
                      <h1 className="text-2xl font-semibold mb-2">{fixture.name}</h1>
                      <p className="text-gray-600">{fixture.description}</p>
                      <p className="text-xs text-gray-500 mt-2">Updated {new Date(fixture.updated_at).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={startEditing}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-md flex items-center gap-1"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Fixture
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {isEditMode ? (
                  <>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                      <div className="flex gap-4 sm:gap-12 text-sm font-medium text-gray-700 w-full min-w-0">
                        <div className="w-1/3 min-w-0">Key</div>
                        <div className="w-1/3 min-w-0">Value</div>
                      </div>
                      
                      <button
                        onClick={addNewVariable}
                        className="flex items-center gap-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md"
                      >
                        <Plus className="w-4 h-4" />
                        Add Variable
                      </button>
                    </div>
                    
                    {/* Edit mode variables */}
                    {Object.entries(editedFixture.variables).map(([key, value], index) => {
                      const isSensitive = key === 'password' || key.includes('token') || key.includes('secret');
                      
                      return (
                        <div key={`edit-${key}-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center py-3 border-b border-gray-100">
                          <div className="md:col-span-4 min-w-0">
                            <input
                              type="text"
                              value={tempKeys[key] || key}
                              onChange={(e) => handleKeyInputChange(key, e.target.value)}
                              onBlur={() => handleKeyBlur(key)}
                              className="w-full min-w-0 px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Key"
                            />
                          </div>
                          <div className="md:col-span-6 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type={isSensitive && !visibleValues.has(key) ? "password" : "text"}
                                value={value.toString()}
                                onChange={(e) => handleVariableChange(key, e.target.value)}
                                className="w-full min-w-0 px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Value"
                              />
                              {isSensitive && (
                                <button
                                  onClick={() => toggleValueVisibility(key)}
                                  className="p-2 text-gray-500 hover:text-gray-700 rounded-md border border-gray-200"
                                  title={visibleValues.has(key) ? "Hide value" : "Show value"}
                                >
                                  {visibleValues.has(key) ? (
                                    <EyeOff className="w-5 h-5" />
                                  ) : (
                                    <Eye className="w-5 h-5" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="md:col-span-2 flex justify-end">
                            <button
                              onClick={() => removeVariable(key)}
                              className="p-2 text-red-500 hover:text-red-700 rounded-md"
                              title="Remove variable"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div className="flex gap-4 sm:gap-12 text-sm font-medium text-gray-700 border-b border-gray-200 pb-2 mb-4">
                      <div className="w-1/3 min-w-0">Key</div>
                      <div className="w-1/3 min-w-0">Value</div>
                    </div>
                    
                    {/* View mode variables */}
                    {fixture.variables && Object.entries(fixture.variables).map(([key, value], index) => {
                      const isSensitive = key === 'password' || key.includes('token') || key.includes('secret');
                      
                      return (
                        <div key={key} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center py-3 border-b border-gray-100 group">
                          <div className="md:col-span-3 min-w-0">
                            <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded break-all">{key}</code>
                          </div>
                          <div className="md:col-span-7 min-w-0">
                            <div className="flex items-center gap-2">
                              <code className="text-sm bg-gray-50 px-2 py-1 rounded flex-1 font-mono overflow-x-auto break-all min-w-0">
                                {isSensitive && !visibleValues.has(key) ? '••••••••' : value.toString()}
                              </code>
                            </div>
                          </div>
                          <div className="md:col-span-2 flex justify-end space-x-2">
                            {isSensitive && (
                              <button
                                onClick={() => toggleValueVisibility(key)}
                                className="p-1 text-gray-500 hover:text-gray-700 rounded-md"
                                title={visibleValues.has(key) ? "Hide value" : "Show value"}
                              >
                                {visibleValues.has(key) ? (
                                  <EyeOff className="w-4 h-4" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleCopyValue(key, value)}
                              className="p-1 text-gray-500 hover:text-gray-700 rounded-md"
                              title="Copy value"
                            >
                              {copiedKey === `value_${key}` ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {/* Linked Test Suites Section */}
              <div className="mt-8 border-t border-gray-200 pt-6">
                <h2 className="text-lg font-medium mb-4 flex items-center">
                  <LinkIcon className="w-5 h-5 mr-2 text-gray-500" />
                  Linked Test Suites
                </h2>
                
                {loadingSuites ? (
                  <div className="text-sm text-gray-500">Loading linked test suites...</div>
                ) : linkedSuites.length > 0 ? (
                  <div className="bg-gray-50 rounded-md border border-gray-200">
                    <ul className="divide-y divide-gray-200">
                      {linkedSuites.map(suite => (
                        <li key={suite.id} className="px-4 py-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">{suite.name || suite.suite_name}</p>
                              <p className="text-xs text-gray-500">{suite.id}</p>
                            </div>
                            {suite.is_default && (
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 p-4 rounded-md border border-gray-200">
                    This fixture is not linked to any test suites yet.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Fixture not found</h3>
              <p className="text-gray-600 mb-4">The fixture you're looking for doesn't exist or was deleted</p>
              <Link 
                to="/testdata"
                className="px-4 py-2 bg-blue-600 text-white rounded-md"
              >
                Back to Fixtures
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestDataDetails;
